import { useEffect, useState } from "react";
import { BookMarked, CheckCircle2, ExternalLink, Pencil, Plus, SearchX, Trash2, VideoOff, X } from "lucide-react";
import { toast } from "sonner";
import { api, DOMAINES, domaineLabel, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const MARQUES = ["loxone", "unv", "unifi", "reyee", "daikin", "heiwa", "schneider", "legrand", "autre"];
const LABELS = { loxone: "Loxone", unv: "UNV / Uniview", unifi: "Ubiquiti UniFi", reyee: "Reyee / Ruijie", daikin: "Daikin", heiwa: "Heiwa", schneider: "Schneider", legrand: "Legrand", autre: "Autre" };

const VIDE = {
  domaine: "climatisation",
  marque: "autre",
  symptome: "",
  codes: "",
  causes: "",
  gestes: "",
  quand_bkpro: "",
  video_url: "",
  video_validee: false,
  sources: "",
  publie: true,
};

const versFormulaire = (f) => ({
  domaine: f.domaine,
  marque: f.marque || "autre",
  symptome: f.symptome || "",
  codes: (f.codes || []).join(", "),
  causes: (f.causes || []).join("\n"),
  gestes: (f.gestes || []).join("\n"),
  quand_bkpro: f.quand_bkpro || "",
  video_url: f.video_url || "",
  video_validee: !!f.video_validee,
  sources: (f.sources || []).map((s) => `${s.titre || ""} | ${s.url || ""}`).join("\n"),
  publie: f.publie !== false,
});

const versPayload = (form) => ({
  domaine: form.domaine,
  marque: form.marque,
  symptome: form.symptome.trim(),
  codes: form.codes.split(",").map((c) => c.trim()).filter(Boolean),
  causes: form.causes.split("\n").map((c) => c.trim()).filter(Boolean),
  gestes: form.gestes.split("\n").map((c) => c.trim()).filter(Boolean),
  quand_bkpro: form.quand_bkpro.trim(),
  video_url: form.video_url.trim() || null,
  video_validee: form.video_validee,
  sources: form.sources
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [titre, url] = l.split("|").map((p) => (p || "").trim());
      return url ? { titre, url } : { titre: "Source", url: titre };
    }),
  publie: form.publie,
});

export default function AdminSavoir() {
  const [items, setItems] = useState([]);
  const [lacunes, setLacunes] = useState(null);
  const [domaine, setDomaine] = useState("");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (domaine) params.domaine = domaine;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/admin/savoir", { params });
    setItems(data);
  };

  const loadLacunes = () => api.get("/admin/savoir/lacunes", { params: { jours: 90 } }).then(({ data }) => setLacunes(data));

  useEffect(() => {
    load();
    loadLacunes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domaine]);

  const enregistrer = async () => {
    const payload = versPayload(form);
    if (payload.symptome.length < 5) return toast.error("Décrivez le symptôme");
    setBusy(true);
    try {
      if (editId) await api.put(`/admin/savoir/${editId}`, payload);
      else await api.post("/admin/savoir", payload);
      toast.success(editId ? "Fiche mise à jour" : "Fiche ajoutée à la base IA");
      setForm(null);
      setEditId(null);
      load();
      loadLacunes();
    } catch (e) {
      toast.error(errMsg(e, "Enregistrement impossible"));
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async (f) => {
    await api.delete(`/admin/savoir/${f.id}`);
    toast.success("Fiche retirée");
    load();
    loadLacunes();
  };

  const basculerVideo = async (f) => {
    await api.put(`/admin/savoir/${f.id}`, { ...versPayload(versFormulaire(f)), video_validee: !f.video_validee });
    toast.success(f.video_validee ? "Vidéo masquée aux clients" : "Vidéo validée : visible par les clients");
    load();
  };

  const avecVideo = items.filter((f) => f.video_url).length;
  const validees = items.filter((f) => f.video_validee).length;

  return (
    <div className="space-y-6" data-testid="admin-savoir">
      <div>
        <div className="bk-label mb-2">Base de connaissances IA</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Savoir terrain</h1>
        <p className="text-sm text-slate-600 mt-2">
          Pannes réelles compilées depuis les documentations constructeur, les forums métier, GitHub et YouTube.
          L'assistant s'appuie sur ces fiches pour proposer le bon geste et citer sa source. Une vidéo n'est visible
          par le client que si vous l'avez validée.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="savoir-filtre-domaine"
          value={domaine}
          onChange={(e) => setDomaine(e.target.value)}
          className="h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white"
        >
          <option value="">Tous les domaines</option>
          {DOMAINES.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
        <Input
          data-testid="savoir-recherche"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Rechercher un symptôme ou un code (U4, riso…)"
          className="h-10 max-w-xs"
        />
        <Button data-testid="savoir-recherche-btn" variant="outline" onClick={load} className="h-10">
          Filtrer
        </Button>
        <Button
          data-testid="savoir-nouvelle-btn"
          onClick={() => {
            setForm({ ...VIDE });
            setEditId(null);
          }}
          className="h-10 bg-[#005CFF] hover:bg-[#0047CC]"
        >
          <Plus size={15} className="mr-2" /> Nouvelle fiche
        </Button>
      </div>

      <div className="bk-label flex items-center gap-2" data-testid="savoir-stats">
        <BookMarked size={13} /> {items.length} fiche(s) · {avecVideo} avec vidéo · {validees} vidéo(s) validée(s)
      </div>

      {lacunes?.codes?.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4 space-y-3" data-testid="savoir-lacunes">
          <div className="bk-label flex items-center gap-2 text-amber-700">
            <SearchX size={13} /> Codes cherchés par les clients et absents de la base ({lacunes.total})
          </div>
          <p className="text-xs text-slate-600">
            Relevé des 90 derniers jours, issu des saisies de code et des descriptions de panne. Cliquez sur un code
            pour créer sa fiche : il disparaît de cette liste dès qu'il est couvert.
          </p>
          <div className="space-y-1.5">
            {lacunes.codes.slice(0, 12).map((c) => (
              <div key={c.code} data-testid={`savoir-lacune-${c.code}`} className="flex flex-wrap items-center gap-2 justify-between rounded-lg bg-white border border-amber-200 px-3 py-2">
                <div className="min-w-0">
                  <span className="font-mono-bk text-sm font-semibold text-slate-900">{c.code}</span>
                  <span className="text-xs text-slate-500 ml-2">
                    {c.occurrences} recherche(s) · {c.sources.join(", ")} · dernière {fmtDate(c.derniere_recherche)}
                  </span>
                  {c.exemples?.length > 0 && (
                    <div className="text-[11px] text-slate-400 truncate">« {c.exemples[0]} »</div>
                  )}
                </div>
                <Button
                  data-testid={`savoir-lacune-creer-${c.code}`}
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setForm({ ...VIDE, codes: c.code, symptome: `Code ${c.code} — ` });
                    setEditId(null);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <Plus size={13} className="mr-1" /> Créer la fiche
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {lacunes && lacunes.total === 0 && (
        <p className="text-xs text-slate-500" data-testid="savoir-lacunes-vide">
          Aucun code introuvable sur les 90 derniers jours : {lacunes.codes_couverts} codes constructeur sont couverts
          par la base.
        </p>
      )}

      {form && (
        <div className="rounded-xl border border-[#005CFF]/30 bg-white p-5 space-y-3" data-testid="savoir-form">
          <div className="flex items-center justify-between">
            <div className="bk-label">{editId ? "Modifier la fiche" : "Nouvelle fiche terrain"}</div>
            <button data-testid="savoir-form-close" onClick={() => { setForm(null); setEditId(null); }} className="text-slate-400 hover:text-slate-900">
              <X size={16} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <select
              data-testid="savoir-form-domaine"
              value={form.domaine}
              onChange={(e) => setForm({ ...form, domaine: e.target.value })}
              className="h-11 border border-slate-200 rounded-lg px-3 text-sm"
            >
              {DOMAINES.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
            <select
              data-testid="savoir-form-marque"
              value={form.marque}
              onChange={(e) => setForm({ ...form, marque: e.target.value })}
              className="h-11 border border-slate-200 rounded-lg px-3 text-sm"
            >
              {MARQUES.map((m) => (
                <option key={m} value={m}>{LABELS[m]}</option>
              ))}
            </select>
          </div>
          <Input
            data-testid="savoir-form-symptome"
            value={form.symptome}
            onChange={(e) => setForm({ ...form, symptome: e.target.value })}
            placeholder="Symptôme (ex : Code U4 — défaut de communication intérieur/extérieur)"
            className="h-11"
          />
          <Input
            data-testid="savoir-form-codes"
            value={form.codes}
            onChange={(e) => setForm({ ...form, codes: e.target.value })}
            placeholder="Codes constructeur séparés par des virgules (U4, UF, RISO…)"
            className="h-11"
          />
          <Textarea
            data-testid="savoir-form-causes"
            value={form.causes}
            onChange={(e) => setForm({ ...form, causes: e.target.value })}
            rows={3}
            placeholder="Causes probables — une par ligne"
          />
          <Textarea
            data-testid="savoir-form-gestes"
            value={form.gestes}
            onChange={(e) => setForm({ ...form, gestes: e.target.value })}
            rows={3}
            placeholder="Gestes que le client peut tenter sans risque — un par ligne"
          />
          <Textarea
            data-testid="savoir-form-quand"
            value={form.quand_bkpro}
            onChange={(e) => setForm({ ...form, quand_bkpro: e.target.value })}
            rows={2}
            placeholder="Quand BKPRO doit intervenir"
          />
          <Input
            data-testid="savoir-form-video"
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            placeholder="URL vidéo YouTube (optionnel)"
            className="h-11"
          />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              data-testid="savoir-form-video-validee"
              type="checkbox"
              checked={form.video_validee}
              onChange={(e) => setForm({ ...form, video_validee: e.target.checked })}
            />
            Vidéo validée : visible par les clients dans le résultat de l'assistant
          </label>
          <Textarea
            data-testid="savoir-form-sources"
            value={form.sources}
            onChange={(e) => setForm({ ...form, sources: e.target.value })}
            rows={3}
            placeholder={"Sources — une par ligne au format : Titre | https://url"}
          />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              data-testid="savoir-form-publie"
              type="checkbox"
              checked={form.publie}
              onChange={(e) => setForm({ ...form, publie: e.target.checked })}
            />
            Fiche active dans la recherche IA
          </label>
          <Button data-testid="savoir-form-save" onClick={enregistrer} disabled={busy} className="bg-[#005CFF] hover:bg-[#0047CC]">
            {busy ? "Enregistrement…" : "Enregistrer la fiche"}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} data-testid={`savoir-row-${f.id}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  {f.symptome}
                  {f.codes?.length > 0 && (
                    <span className="ml-2 text-[10px] font-mono rounded bg-slate-100 text-slate-600 px-1.5 py-0.5">
                      {f.codes.join(" · ")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {domaineLabel(f.domaine)} · {LABELS[f.marque] || f.marque} · {(f.gestes || []).length} geste(s) ·{" "}
                  {(f.sources || []).length} source(s)
                  {f.publie === false ? " · inactive" : ""}
                </div>
                {f.sources?.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 mt-1">
                    {f.sources.map((s, i) => (
                      <a
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#005CFF]"
                      >
                        <ExternalLink size={10} /> {s.titre || s.url}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {f.video_url && (
                  <button
                    data-testid={`savoir-video-toggle-${f.id}`}
                    onClick={() => basculerVideo(f)}
                    title={f.video_validee ? "Vidéo visible par les clients" : "Vidéo masquée : cliquez pour valider"}
                    className={f.video_validee ? "text-emerald-500" : "text-slate-300 hover:text-emerald-500"}
                  >
                    {f.video_validee ? <CheckCircle2 size={16} /> : <VideoOff size={16} />}
                  </button>
                )}
                <button
                  data-testid={`savoir-edit-${f.id}`}
                  onClick={() => {
                    setForm(versFormulaire(f));
                    setEditId(f.id);
                  }}
                  className="text-slate-300 hover:text-[#005CFF]"
                >
                  <Pencil size={15} />
                </button>
                <button data-testid={`savoir-delete-${f.id}`} onClick={() => supprimer(f)} className="text-slate-300 hover:text-red-500">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="savoir-empty">
            Aucune fiche pour ce filtre.
          </p>
        )}
      </div>
    </div>
  );
}
